import { useMemo, useState } from 'react'
import { useArchived } from '../contexts/ArchiveContext'
import { Icon } from './Icon'
import { PlanTaskEditor } from './PlanTaskEditor'
import {
  useEventPlanTasks,
  useCreatePlanTask,
  usePatchPlanTask,
  useDeletePlanTask
} from '../hooks/usePlanTasks'
import { parseIsoDate } from '../lib/dates'
import type { PlanTask, PlanGroup } from '../types'

// PlanTab — Project Plan / Gantt (spec 32). Read + create + edit +
// delete. Rows grouped by `group`, each rendering a horizontal bar
// positioned by start/end dates inside a month-banded timeline.
// Progress shown as a solid darker overlay on the left portion of each
// bar. "Today" marked as a vertical oxblood line. Click a row to open
// the edit modal; "+ Add Task" creates a new one.
//
// Still deferred to follow-ups (snags for full spec 32):
//   - Drag-to-resize / drag-to-move bars (UX-only).
//   - Dependency arrows (dependsOnIds CRUD + SVG renderer).
//   - Group filter / range filter.
//   - Reusable GanttStrip primitive at src/components/Gantt/.
//
// Range is auto-fit from min(startAt)…max(endAt) across all tasks,
// padded by one week either side. If no tasks exist, falls back to a
// 6-month window centred on today.

interface Props {
  eventId: number
  // Optional — when set, new-task creation defaults its start to the
  // event start date so the first task lands inside the event window
  // rather than wherever "today" happens to be.
  eventStartDate?: string
}

const PLAN_GROUP_COLOUR: Record<PlanGroup, string> = {
  Bookings:   '#0a4a3a',  // green
  Marketing:  '#9b7f2a',  // brass
  Programme:  '#3d5a6c',  // slate
  Safety:     '#7a2329',  // oxblood
  Staffing:   '#c98a4e',  // amber
  Operations: '#0a4a3a',  // green (intentional repeat per spec 32 §2)
  Live:       '#7a2329',  // oxblood (intentional repeat)
  Closeout:   '#5a5a5a'   // ink-mid
}

const GROUPS: ReadonlyArray<PlanGroup> = [
  'Bookings', 'Marketing', 'Programme', 'Safety',
  'Staffing', 'Operations', 'Live', 'Closeout'
]

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]

const LABEL_WIDTH = 240   // px column for task name + meta
const ROW_HEIGHT  = 36    // px per task row

export function PlanTab({ eventId, eventStartDate }: Props) {
  const archived = useArchived()
  const { data: tasks, isLoading, error } = useEventPlanTasks(eventId)
  const createMut = useCreatePlanTask(eventId)
  const patchMut = usePatchPlanTask(eventId)
  const deleteMut = useDeletePlanTask(eventId)

  const [editing, setEditing] = useState<PlanTask | 'new' | null>(null)

  // Group tasks for rendering. Returned by the API already sorted by
  // group then start; the grouping is just visual.
  const grouped = useMemo(() => {
    if (!tasks) return [] as Array<{ group: PlanGroup; tasks: PlanTask[] }>
    const map = new Map<PlanGroup, PlanTask[]>()
    for (const t of tasks) {
      const arr = map.get(t.group) ?? []
      arr.push(t)
      map.set(t.group, arr)
    }
    return GROUPS.filter((g) => map.has(g)).map((g) => ({
      group: g,
      tasks: map.get(g)!
    }))
  }, [tasks])

  // Auto-fit range: min(startAt)…max(endAt) + 1 week padding either side.
  const range = useMemo(() => computeRange(tasks ?? []), [tasks])

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading project plan…</div>
  }
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load project plan</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  const hasTasks = tasks != null && tasks.length > 0
  const months = hasTasks ? monthsBetween(range.start, range.end) : []
  const todayPct = hasTasks ? pctOf(new Date(), range.start, range.end) : -1

  // editing state -> initial PlanTask | null fed into the editor
  const editingInitial: PlanTask | null = editing === 'new' || editing == null ? null : editing
  const editorMode: 'create' | 'edit' = editing === 'new' ? 'create' : 'edit'

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h2 className="font-display text-xl">Project Plan</h2>
          <p className="text-sm text-ink-soft mt-0.5">
            Gantt view of the timeline-bound build work. Click a row to edit;
            drag-to-resize bars and dependency arrows land in a follow-up.
          </p>
        </div>
        {!archived && (
          <button
            type="button"
            onClick={() => setEditing('new')}
            className="px-3 py-1.5 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 flex items-center gap-1.5"
          >
            <Icon name="plus" size={14} />
            Add task
          </button>
        )}
      </div>

      {!hasTasks ? (
        <div className="bg-white border border-line rounded p-10 text-center text-sm text-ink-soft">
          <div className="font-display text-lg mb-1 text-ink">No project plan yet</div>
          <p className="mb-3">
            Tasks added here will appear as Gantt rows grouped by Bookings,
            Marketing, Programme, Safety, Staffing, Operations, Live, and Closeout.
          </p>
          {!archived && (
            <button
              type="button"
              onClick={() => setEditing('new')}
              className="text-sm text-green font-semibold hover:underline"
            >
              Add the first task
            </button>
          )}
        </div>
      ) : (
      <div className="bg-white border border-line rounded overflow-hidden">
        {/* Header row: label column + month band */}
        <div
          className="flex border-b border-line bg-parchment/60"
          style={{ height: ROW_HEIGHT }}
        >
          <div
            className="flex-shrink-0 border-r border-line px-3 flex items-center text-[10px] uppercase tracking-wider font-semibold text-ink-soft"
            style={{ width: LABEL_WIDTH }}
          >
            Task
          </div>
          <div className="relative flex-1">
            <div className="absolute inset-0 flex">
              {months.map((m, i) => (
                <div
                  key={`${m.year}-${m.month}`}
                  className="flex items-center justify-center text-[10px] uppercase tracking-wider font-semibold text-ink-soft"
                  style={{
                    flex: m.widthFraction,
                    borderRight: i === months.length - 1 ? 'none' : '1px solid #ece6db'
                  }}
                >
                  {MONTH_LABELS[m.month]}{m.showYear ? ` ${m.year}` : ''}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Body: group blocks */}
        {grouped.map(({ group, tasks }) => (
          <div key={group}>
            {/* Group header row */}
            <div
              className="flex border-b border-line"
              style={{ height: 26, background: '#faf6ee' }}
            >
              <div
                className="flex-shrink-0 border-r border-line px-3 flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold"
                style={{ width: LABEL_WIDTH, color: PLAN_GROUP_COLOUR[group] }}
              >
                <span
                  aria-hidden
                  className="w-2 h-2 rounded-full"
                  style={{ background: PLAN_GROUP_COLOUR[group] }}
                />
                {group}
                <span className="text-ink-soft ml-1 font-normal normal-case tracking-normal text-xs">
                  ({tasks.length})
                </span>
              </div>
              <div className="flex-1" />
            </div>

            {/* Task rows */}
            {tasks.map((t) => (
              <PlanRow
                key={t.id}
                task={t}
                rangeStart={range.start}
                rangeEnd={range.end}
                onClick={archived ? undefined : () => setEditing(t)}
              />
            ))}
          </div>
        ))}

        {/* Today marker overlay — positioned absolutely against the body */}
        {todayPct >= 0 && todayPct <= 100 && (
          <div
            aria-hidden
            className="pointer-events-none relative"
            style={{ height: 0 }}
          >
            {/* Empty container — the actual line is rendered per row via
                a CSS gradient below. Kept here so the future "show today
                dot at top" addition has a sensible anchor. */}
          </div>
        )}
      </div>
      )}

      {/* Legend — only meaningful when there are bars to colour-code */}
      {hasTasks && (
      <div className="flex flex-wrap gap-3 text-xs text-ink-soft">
        {GROUPS.map((g) => (
          <span key={g} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className="w-2.5 h-2.5 rounded-sm"
              style={{ background: PLAN_GROUP_COLOUR[g] }}
            />
            {g}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 ml-auto">
          <span aria-hidden className="w-0.5 h-3 bg-oxblood" />
          Today
        </span>
      </div>
      )}

      {!archived && editing != null && (
        <PlanTaskEditor
          mode={editorMode}
          initial={editingInitial}
          saving={createMut.isPending || patchMut.isPending}
          deleting={deleteMut.isPending}
          serverError={(createMut.error ?? patchMut.error ?? deleteMut.error) as Error | null}
          defaultStartAt={eventStartDate}
          onClose={() => setEditing(null)}
          onSave={(body) => {
            if (editing === 'new') {
              createMut.mutate(body, { onSuccess: () => setEditing(null) })
            } else {
              patchMut.mutate(
                { id: editing.id, body },
                { onSuccess: () => setEditing(null) }
              )
            }
          }}
          onDelete={editing !== 'new' ? () => {
            const ok = window.confirm(`Delete plan task "${editing.name}"? This can't be undone.`)
            if (!ok) return
            deleteMut.mutate(editing.id, { onSuccess: () => setEditing(null) })
          } : undefined}
        />
      )}
    </div>
  )
}

// ---- Row -----------------------------------------------------------------

function PlanRow({
  task, rangeStart, rangeEnd, onClick
}: {
  task: PlanTask
  rangeStart: Date
  rangeEnd: Date
  onClick: (() => void) | undefined
}) {
  const start = parseIsoDate(task.startAt)
  const end = parseIsoDate(task.endAt)
  const leftPct = pctOf(start, rangeStart, rangeEnd)
  const rightPct = pctOf(end, rangeStart, rangeEnd)
  const widthPct = Math.max(0.5, rightPct - leftPct)
  const todayPct = pctOf(new Date(), rangeStart, rangeEnd)

  const colour = PLAN_GROUP_COLOUR[task.group]
  const isComplete = task.progress >= 100
  const interactive = onClick != null

  return (
    <div
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : -1}
      onKeyDown={(e) => {
        if (!interactive) return
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick!() }
      }}
      className={
        'flex border-b border-line last:border-b-0 transition-colors ' +
        (interactive
          ? 'hover:bg-parchment/40 cursor-pointer focus:outline-none focus:ring-2 focus:ring-green/30'
          : '')
      }
      style={{ height: ROW_HEIGHT }}
      title={`${task.name} · ${task.startAt} → ${task.endAt} · ${task.progress}%`}
    >
      <div
        className="flex-shrink-0 border-r border-line px-3 py-1.5 min-w-0"
        style={{ width: LABEL_WIDTH }}
      >
        <div className="text-sm font-semibold truncate">{task.name}</div>
        <div className="text-[11px] text-ink-soft truncate">
          {task.assigneeName ?? <span className="italic">unassigned</span>}
        </div>
      </div>
      <div className="relative flex-1 overflow-hidden">
        {/* Today line within this row */}
        {todayPct >= 0 && todayPct <= 100 && (
          <span
            aria-hidden
            className="absolute top-0 bottom-0 w-px bg-oxblood opacity-70"
            style={{ left: `${todayPct}%` }}
          />
        )}
        {/* Task bar */}
        <div
          className="absolute top-1.5 bottom-1.5 rounded overflow-hidden flex items-center"
          style={{
            left: `${leftPct}%`,
            width: `${widthPct}%`,
            background: `${colour}22`,                // 13% tint for the empty bar
            border: `1px solid ${colour}`,
            opacity: isComplete ? 0.65 : 1
          }}
        >
          {/* Progress fill */}
          {task.progress > 0 && (
            <div
              className="absolute top-0 bottom-0 left-0"
              style={{
                width: `${task.progress}%`,
                background: colour
              }}
            />
          )}
          {/* Progress % label — only shown if bar is wide enough */}
          {widthPct > 5 && (
            <span
              className="relative px-2 text-[10px] font-mono font-bold"
              style={{
                color: task.progress >= 50 ? '#fff' : colour,
                textShadow: task.progress >= 50 ? '0 1px 2px rgba(0,0,0,0.3)' : undefined
              }}
            >
              {task.progress}%
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// ---- Helpers -------------------------------------------------------------

// Compute the auto-fit range: min(startAt)…max(endAt) over the tasks,
// padded by 7 days either side. Empty list → 6-month window centred
// on today.
function computeRange(tasks: PlanTask[]): { start: Date; end: Date } {
  if (tasks.length === 0) {
    const now = new Date()
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 3, 1))
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 3, 1))
    return { start, end }
  }
  let minStart = Infinity
  let maxEnd = -Infinity
  for (const t of tasks) {
    const s = parseIsoDate(t.startAt).getTime()
    const e = parseIsoDate(t.endAt).getTime()
    if (s < minStart) minStart = s
    if (e > maxEnd) maxEnd = e
  }
  return {
    start: new Date(minStart - 7 * 86_400_000),
    end:   new Date(maxEnd   + 7 * 86_400_000)
  }
}

// Percentage of `date` between rangeStart and rangeEnd. Clamped at 0/100.
function pctOf(date: Date, start: Date, end: Date): number {
  const total = end.getTime() - start.getTime()
  if (total <= 0) return 0
  const pct = ((date.getTime() - start.getTime()) / total) * 100
  if (pct < 0) return -1   // sentinel — caller may skip rendering
  if (pct > 100) return 101
  return pct
}

// Month bands for the header row. Each band's widthFraction is its
// share of the total range — so a 15-day August inside a 6-month range
// renders correctly as roughly 8% wide rather than 16% (a naive
// 1/months count).
function monthsBetween(
  start: Date,
  end: Date
): Array<{ year: number; month: number; widthFraction: number; showYear: boolean }> {
  const out: Array<{ year: number; month: number; widthFraction: number; showYear: boolean }> = []
  const total = end.getTime() - start.getTime()
  if (total <= 0) return out

  const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1))
  let prevYear: number | null = null
  while (cursor.getTime() < end.getTime()) {
    const monthStart = new Date(Math.max(start.getTime(), cursor.getTime()))
    const nextCursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1))
    const monthEnd = new Date(Math.min(end.getTime(), nextCursor.getTime()))
    const span = monthEnd.getTime() - monthStart.getTime()
    if (span > 0) {
      out.push({
        year: cursor.getUTCFullYear(),
        month: cursor.getUTCMonth(),
        widthFraction: span / total,
        showYear: cursor.getUTCMonth() === 0 || prevYear !== cursor.getUTCFullYear()
      })
      prevYear = cursor.getUTCFullYear()
    }
    cursor.setTime(nextCursor.getTime())
  }
  return out
}
